import React, { useState, useEffect, useContext } from "react";
import { Suppliercredit, SupplierPurchaseDetails, postGeneralData, UpdatePaymentComplete, getBankBalance } from "../../../Utility/Api";
import { Pagination } from "../../Common/Functions";
import { toast } from "react-toastify";
import { WarehouseContext } from "../../../Utility/UserContext";
import { getFormattedCurrentDate2, NaviGate, onReqCreatePayment, shop_id } from "../../../Utility/function";

/* ─────────────────────────────────────────────
   Unified Supplier Transaction Report
   - One screen combining the old two-page flow
   - Summary cards + supplier table + pay modal
───────────────────────────────────────────── */
const SupplierRecept = () => {
  const { showLoader, hideLoader, UserItem, shopItem } = useContext(WarehouseContext);

  // ── Supplier list ──
  const [suppliers, setSuppliers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const itemsPerPage = 10;

  // ── Selected supplier + its purchases ──
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [purchasePage, setPurchasePage] = useState(1);
  const [purchaseTotalPages, setPurchaseTotalPages] = useState(1);

  // ── Date filter ──
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // ── Summary computed from purchases of selected supplier ──
  const [summary, setSummary] = useState({ totalPurchases: 0, totalPaid: 0, creditBalance: 0, currentBalance: 0 });

  // ── Pay modal ──
  const [payModal, setPayModal] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [cashAmount, setCashAmount] = useState("");
  const [cardAmount, setCardAmount] = useState("");
  const [bankBalance, setBankBalance] = useState(null);

  // ── On mount: load supplier list ──
  useEffect(() => {
    fetchSupplierList(1, searchQuery);
    fetchBankBalance();
  }, []);

  const fetchBankBalance = async () => {
    try {
      const res = await getBankBalance(shop_id);
      if (res.data?.status === "success") {
        setBankBalance(res.data.bank_balance);
      }
    } catch (e) {
      console.error("Error fetching bank balance", e);
    }
  };

  useEffect(() => {
    fetchSupplierList(1, searchQuery);
    setCurrentPage(1);
  }, [searchQuery]);

  const fetchSupplierList = async (page, search = searchQuery) => {
    showLoader();
    try {
      const res = await Suppliercredit(shop_id, page, search);
      if (res.data?.data) {
        setSuppliers(res.data.data);
        setTotalPages(res.data.pagination?.total_pages || 1);
      }
    } catch (e) {
      console.error(e);
    } finally {
      hideLoader();
    }
  };

  const handlePageChange = (p) => {
    setCurrentPage(p);
    fetchSupplierList(p, searchQuery);
  };

  // ── When user clicks "View Report" for a supplier ──
  const handleViewReport = async (supplier) => {
    setSelectedSupplier(supplier);
    setPurchasePage(1);
    await fetchPurchases(supplier.supplier_id, 1);
  };

  const fetchPurchases = async (supplierId, page) => {
    showLoader();
    try {
      const res = await SupplierPurchaseDetails(supplierId, shop_id, page);
      if (res.data?.data) {
        const data = res.data.data;
        setPurchases(data);
        setPurchaseTotalPages(res.data.pagination?.total_pages || 1);

        // ── Compute summary ──
        const totalPurchases = data.reduce((s, p) => s + parseFloat(p.total_amount || 0), 0);
        const totalPaid = data.reduce((s, p) => s + (parseFloat(p.total_amount || 0) - parseFloat(p.remaining || 0)), 0);
        const creditBalance = data.reduce((s, p) => s + parseFloat(p.remaining || 0), 0);
        setSummary({
          totalPurchases,
          totalPaid,
          creditBalance,
          currentBalance: creditBalance,
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      hideLoader();
    }
  };

  // ── Pay logic (preserved from old screen) ──
  const dueAmount = parseFloat(payTarget?.remaining || 0);
  const totalPayment = parseFloat(cashAmount || 0) + parseFloat(cardAmount || 0);
  const remainingAfterPay = dueAmount - totalPayment;

  const openPayModal = (purchase) => {
    setPayTarget(purchase);
    setCashAmount("");
    setCardAmount("");
    setPayModal(true);
  };

  const handleCashChange = (e) => {
    const v = e.target.value;
    if (v === "" || (parseFloat(v) + parseFloat(cardAmount || 0) <= dueAmount)) setCashAmount(v);
  };

  const handleCardChange = (e) => {
    const v = e.target.value;
    if (v === "" || (parseFloat(v) + parseFloat(cashAmount || 0) <= dueAmount)) setCardAmount(v);
  };

  const onPayCredit = async () => {
    if (!payTarget?.purchase_id) { toast.error("Invalid purchase details"); return; }
    const cash = parseFloat(cashAmount || 0);
    const card = parseFloat(cardAmount || 0);
    const totalToPay = cash + card;

    if (totalToPay <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (bankBalance !== null && totalToPay > bankBalance) {
      toast.error(`Insufficient Bank Balance! Available: ${bankBalance.toFixed(3)}`);
      return;
    }

    try {
      showLoader();
      let paydata = {
        orderID: payTarget.po,
        date: getFormattedCurrentDate2(),
        amount: "0",
        total_amount: payTarget.total_amount,
        cash,
        credit: 0,
        card,
        type: 1,
        mop: cash && card ? "1,4" : cash ? "1" : "4",
        complete: "1",
      };
      if (remainingAfterPay > 0) {
        paydata.credit = remainingAfterPay;
        paydata.mop += ",2";
        paydata.complete = "0";
      }

      await onReqCreatePayment(paydata);

      // Bank credit entry (cash/card paid to supplier)
      await postGeneralData({
        type: "2",
        ref_id: payTarget.po,
        remarks: "Bank - To supplier",
        date: getFormattedCurrentDate2(),
        created_by: UserItem?.user_id,
        shop_id: shopItem?.id || "0",
        amount: cash + card,
        is_debit: "0",
      });

      if (paydata.complete === "0") {
        await postGeneralData({
          type: "7",
          ref_id: payTarget.po,
          remarks: "Purchase Credit Remaining",
          date: getFormattedCurrentDate2(),
          created_by: UserItem?.user_id,
          shop_id: shopItem?.id || "0",
          amount: remainingAfterPay,
          is_debit: "0",
        });
      } else {
        await UpdatePaymentComplete({ type: "7", shop_id: shopItem?.id || "0", ref_id: payTarget.po });
      }

      toast.success("Payment recorded successfully");
      setPayModal(false);
      await fetchPurchases(selectedSupplier.supplier_id, purchasePage);
      fetchSupplierList(currentPage, searchQuery);
      fetchBankBalance(); // Refresh bank balance after payment
    } catch (e) {
      console.error(e);
      toast.error("Error processing payment");
    } finally {
      hideLoader();
    }
  };

  // ── Summary card component ──
  const SummaryCard = ({ label, value, color = "#111827" }) => (
    <div style={{
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      padding: "20px 24px",
      flex: 1,
      minWidth: 160,
    }}>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>{label}</div>
    </div>
  );

  return (
    <div className="page-wrappers">
      <div className="page-content">
        <NaviGate />

        {/* ── Page title ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <span style={{ fontSize: 20 }}>🚚</span>
          <h5 style={{ margin: 0, fontWeight: 700, fontSize: 18 }}>Supplier Transaction Report</h5>
        </div>

        {/* ── Filter card ── */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "20px 24px", marginBottom: 20 }}>
          <div style={{ fontWeight: 600, marginBottom: 14, fontSize: 14 }}>Select Supplier &amp; Date Range</div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
            {/* Supplier filter */}
            <div style={{ flex: "1 1 200px" }}>
              <label style={{ fontSize: 12, color: "#374151", marginBottom: 4, display: "block" }}>Supplier</label>
              <select
                className="form-select form-select-sm"
                value={selectedSupplier?.supplier_id || ""}
                onChange={(e) => {
                  const s = suppliers.find(x => String(x.supplier_id) === e.target.value);
                  if (s) handleViewReport(s);
                  else { setSelectedSupplier(null); setPurchases([]); setSummary({ totalPurchases: 0, totalPaid: 0, creditBalance: 0, currentBalance: 0 }); }
                }}
              >
                <option value="">All Suppliers</option>
                {suppliers.map(s => (
                  <option key={s.supplier_id} value={s.supplier_id}>{s.supplier_name}</option>
                ))}
              </select>
            </div>

            {/* From Date */}
            <div style={{ flex: "1 1 160px" }}>
              <label style={{ fontSize: 12, color: "#374151", marginBottom: 4, display: "block" }}>From Date</label>
              <input type="date" className="form-control form-control-sm" value={fromDate} onChange={e => setFromDate(e.target.value)} />
            </div>

            {/* To Date */}
            <div style={{ flex: "1 1 160px" }}>
              <label style={{ fontSize: 12, color: "#374151", marginBottom: 4, display: "block" }}>To Date</label>
              <input type="date" className="form-control form-control-sm" value={toDate} onChange={e => setToDate(e.target.value)} />
            </div>

            {/* Generate Button */}
            <div style={{ flex: "0 0 auto" }}>
              {/* <div style={{ flex: "0 0 auto", display: "none" }}> */}
              <button
                className="btn btn-sm btn-primary"
                style={{ color: "#fff", borderRadius: 8, padding: "6px 18px", display: "flex", alignItems: "center", gap: 6 }}
                disabled={!selectedSupplier}
                onClick={() => selectedSupplier ? fetchPurchases(selectedSupplier.supplier_id, 1) : fetchSupplierList(1, searchQuery)}
              >
                📄 Generate Report
              </button>
            </div>
          </div>
        </div>

        {/* ── Summary cards (only when a supplier is selected) ── */}
        {selectedSupplier && (
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
            <SummaryCard label="Total Purchases" value={`${summary.totalPurchases.toFixed(3)}`} />
            <SummaryCard label="Total Paid" value={`${summary.totalPaid.toFixed(3)}`} color="#16a34a" />
            <SummaryCard label="Credit Balance" value={`${summary.creditBalance.toFixed(3)}`} color="#ea580c" />
            <SummaryCard label="Current Balance" value={`${summary.currentBalance.toFixed(3)}`} color="#dc2626" />
          </div>
        )}

        {/* ── Purchase details table (when supplier selected) ── */}
        {selectedSupplier ? (
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "20px 24px", marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>
                Purchases — <span style={{ color: "#1e3a8a" }}>{selectedSupplier.supplier_name}</span>
              </span>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => { setSelectedSupplier(null); setPurchases([]); }}
              >
                ← Back to All Suppliers
              </button>
            </div>

            <div className="table-responsive">
              <table className="table table-striped table-sm table-bordered align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>S.No</th>
                    <th>Purchase No</th>
                    <th>Total Amount (INR)</th>
                    <th>Paid (INR)</th>
                    <th>Due Amount (INR)</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {purchases.length > 0 ? purchases.map((p, i) => {
                    const paid = parseFloat(p.total_amount || 0) - parseFloat(p.remaining || 0);
                    const isPaid = p.purchase_unit == 1 || parseFloat(p.remaining || 0) <= 0;
                    return (
                      <tr key={p.purchase_id}>
                        <td>{(purchasePage - 1) * itemsPerPage + i + 1}</td>
                        <td style={{ fontWeight: 600 }}>{p.po}</td>
                        <td>{Number(p.total_amount).toFixed(3)}</td>
                        <td style={{ color: "#16a34a", fontWeight: 600 }}>{paid.toFixed(3)}</td>
                        <td style={{ color: "#dc2626", fontWeight: 600 }}>{Number(p.remaining).toFixed(3)}</td>
                        <td>
                          <span style={{
                            padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                            background: isPaid ? "#dcfce7" : "#fef3c7",
                            color: isPaid ? "#16a34a" : "#b45309",
                          }}>
                            {isPaid ? "Paid" : "Pending"}
                          </span>
                        </td>
                        <td>
                          {isPaid
                            ? <span style={{ color: "#16a34a", fontWeight: 600, fontSize: 13 }}>✓ Cleared</span>
                            : <button className="btn btn-warning btn-sm" onClick={() => openPayModal(p)}>Pay</button>
                          }
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan="7" className="text-center text-muted py-3">No purchase records found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination currentPage={purchasePage} totalPages={purchaseTotalPages} onPageChange={(p) => { setPurchasePage(p); fetchPurchases(selectedSupplier.supplier_id, p); }} />
          </div>
        ) : (
          /* ── All Suppliers table ── */
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "20px 24px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>All Suppliers</span>
              {/* Search */}
              <div style={{ position: "relative", width: 240 }}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search supplier..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #f3f4f6" }}>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Phone</th>
                    <th style={thStyle}>Total Purchases</th>
                    <th style={thStyle}>Credit Balance (INR)</th>
                    {/* <th style={thStyle}>Status</th> */}
                    <th style={thStyle}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {suppliers.length > 0 ? suppliers.map((s, i) => (
                    <tr key={s.supplier_id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={tdStyle}>{s.supplier_name}</td>
                      <td style={tdStyle}>{s.supplier_phone || "—"}</td>
                      <td style={tdStyle}>{s.total_purchases ?? "—"}</td>
                      <td style={{ ...tdStyle, color: parseFloat(s.remaining || 0) > 0 ? "#dc2626" : "#16a34a", fontWeight: 600 }}>
                        {parseFloat(s.remaining || 0).toFixed(3)}
                      </td>
                      {/* <td style={tdStyle}>
                        <span style={{
                          padding: "3px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                          background: "#1e3a8a", color: "#fff",
                        }}>active</span>
                      </td> */}
                      <td style={tdStyle}>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          style={{ fontSize: 12, borderRadius: 6 }}
                          onClick={() => handleViewReport(s)}
                        >
                          View Report
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="6" className="text-center text-muted py-4">No suppliers found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          </div>
        )}

        {/* ── Pay modal ── */}
        {payModal && (
          <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.4)" }} tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content p-3" style={{ borderRadius: 12, boxShadow: "0 4px 24px rgba(0,0,0,0.15)" }}>
                <div className="modal-header border-0">
                  <h5 className="modal-title fw-bold">Payment Details</h5>
                  <button type="button" className="btn-close" onClick={() => setPayModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="p-3 bg-light rounded">
                    <p className="mb-2">Order ID: <strong>{payTarget?.po}</strong></p>
                    <p className="mb-2">Supplier: <strong>{payTarget?.supplier_name || selectedSupplier?.supplier_name}</strong></p>
                    <hr />
                    {bankBalance !== null && (
                      <div className="d-flex justify-content-between mb-2">
                        <span>Current Bank Balance:</span>
                        <strong className={bankBalance < totalPayment ? "text-danger" : "text-success"}>
                          {bankBalance.toFixed(3)}
                        </strong>
                      </div>
                    )}
                    <div className="d-flex justify-content-between mb-2">
                      <span>Total Amount:</span>
                      <strong className="text-primary">{Number(payTarget?.total_amount).toFixed(3)}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-3">
                      <span>Due Amount:</span>
                      <strong className="text-danger">{Number(payTarget?.remaining).toFixed(3)}</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span>Cash:</span>
                      <input type="number" className="form-control text-end" style={{ width: 130 }} value={cashAmount} onChange={handleCashChange} />
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span>Card:</span>
                      <input type="number" className="form-control text-end" style={{ width: 130 }} value={cardAmount} onChange={handleCardChange} />
                    </div>
                    <hr />
                    <div className="d-flex justify-content-between fw-bold fs-6">
                      <span>Remaining After Pay:</span>
                      <span className={remainingAfterPay > 0 ? "text-warning" : "text-success"}>{remainingAfterPay.toFixed(3)}</span>
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0">
                  <button className="btn btn-secondary" onClick={() => setPayModal(false)}>Cancel</button>
                  <button
                    className="btn btn-success"
                    disabled={totalPayment <= 0 || totalPayment > dueAmount}
                    onClick={onPayCredit}
                  >
                    Pay Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Table style helpers ──
const thStyle = { padding: "10px 14px", fontWeight: 600, fontSize: 13, color: "#374151", background: "#f9fafb" };
const tdStyle = { padding: "10px 14px", fontSize: 13, color: "#374151" };

export default SupplierRecept;
